import * as React from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import Cookies from "universal-cookie";
import Logo from "../../EasyAgility.png";
import { Menu, MenuItem, ListItemIcon, ListItemText } from "@mui/material";
import {
  Pets,
  EmojiEvents,
  Groups,
  ManageAccounts,
  Policy,
  AdminPanelSettings,
  DirectionsRun,
  Sell,
} from "@mui/icons-material";
import { Link, useLocation } from "react-router-dom";
import { isAdmin, isClubAdmin } from "../../utils/jwt";
import { featureFlags } from "../../utils/featureFlags";
import { useHasManagedClubs } from "../AgilityClubs/useHasManagedClubs";

const TopNav = ({
  setLoggedIn,
}: {
  setLoggedIn: (loggedIn: boolean) => void;
}) => {
  const location = useLocation();
  const [currentPageName, setCurrentPageName] = React.useState(() => {
    const path = location.pathname;
    if (path.includes("dogs")) return "My Dogs";
    if (path.includes("handlers")) return "My Handlers";
    if (featureFlags.clubMemberships && path.includes("club-memberships"))
      return "Club Memberships";
    if (featureFlags.clubAdministration && path.includes("agility-clubs"))
      return "Club Administration";
    if (path.includes("show-management") && path.includes("entries"))
      return "Show Entries";
    if (path.includes("show-management")) return "Show Management";
    return "Home";
  });
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [userIsAdmin, setUserIsAdmin] = React.useState(false);
  const [userIsClubAdmin, setUserIsClubAdmin] = React.useState(false);
  const { hasManagedClubs } = useHasManagedClubs(
    featureFlags.clubAdministration,
    location.pathname,
  );
  const showClubAdminNav =
    featureFlags.clubAdministration && (userIsClubAdmin || hasManagedClubs);
  const open = Boolean(anchorEl);

  React.useEffect(() => {
    setUserIsAdmin(isAdmin());
    setUserIsClubAdmin(isClubAdmin());
    // Update page name based on current route
    const path = location.pathname;
    if (path.includes("dogs")) {
      setCurrentPageName("My Dogs");
    } else if (path.includes("handlers")) {
      setCurrentPageName("My Handlers");
    } else if (
      featureFlags.clubMemberships &&
      path.includes("club-memberships")
    ) {
      setCurrentPageName("Club Memberships");
    } else if (
      featureFlags.clubAdministration &&
      path.includes("agility-clubs")
    ) {
      setCurrentPageName("Club Administration");
    } else if (path.includes("show-management") && path.includes("entries")) {
      setCurrentPageName("Show Entries");
    } else if (path.includes("show-management")) {
      setCurrentPageName("Show Management");
    } else {
      setCurrentPageName("Home");
    }
  }, [location.pathname]);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    const cookies = new Cookies();
    cookies.remove("token");
    setLoggedIn(false);
  };

  const toggleMenuOpen = (event: any) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(!menuOpen);
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar
        position="static"
        elevation={2}
        sx={{
          bgcolor: "#00395A",
          backgroundImage:
            "linear-gradient(135deg, #00395A 0%, #1a5a7a 100%)",
        }}
      >
        <Toolbar sx={{ minHeight: 64, px: { xs: 2, sm: 3 }, gap: 1.5 }}>
          <IconButton
            size="medium"
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={toggleMenuOpen}
            sx={{ opacity: 0.85, "&:hover": { opacity: 1, bgcolor: "rgba(255,255,255,0.1)" } }}
          >
            <MenuIcon />
          </IconButton>

          {/* Logo in white tile */}
          <Box
            sx={{
              bgcolor: "white",
              borderRadius: "8px",
              p: "5px 6px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
              flexShrink: 0,
            }}
          >
            <img
              src={Logo}
              alt="Easy Agility"
              style={{ height: 28, width: "auto", display: "block" }}
            />
          </Box>

          {/* Brand name */}
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              letterSpacing: 0.3,
              color: "white",
              flexShrink: 0,
              display: { xs: "none", sm: "block" },
            }}
          >
            Easy Agility
          </Typography>

          <Divider
            orientation="vertical"
            flexItem
            sx={{
              borderColor: "rgba(255,255,255,0.22)",
              my: 1.5,
              display: { xs: "none", sm: "block" },
            }}
          />

          {/* Current page */}
          <Typography
            variant="body1"
            sx={{ flexGrow: 1, color: "rgba(255,255,255,0.8)", fontWeight: 500 }}
          >
            {currentPageName}
          </Typography>

          <Button
            onClick={handleLogout}
            color="inherit"
            startIcon={<LogoutIcon sx={{ fontSize: 18 }} />}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.875rem",
              opacity: 0.85,
              "&:hover": { opacity: 1, bgcolor: "rgba(255,255,255,0.1)" },
              borderRadius: 2,
              px: 2,
              py: 0.75,
              flexShrink: 0,
            }}
          >
            Logout
          </Button>
        </Toolbar>
      </AppBar>

      <Menu
        anchorEl={anchorEl}
        id="menu"
        open={open}
        onClose={handleClose}
        PaperProps={{
          elevation: 4,
          sx: {
            mt: 0.5,
            minWidth: 220,
            borderRadius: 2,
            overflow: "visible",
            "& .MuiMenuItem-root": {
              borderRadius: 1,
              mx: 0.5,
              "&:hover": { bgcolor: "rgba(0,57,90,0.07)" },
            },
          },
        }}
        transformOrigin={{ horizontal: "left", vertical: "top" }}
        anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} letterSpacing={0.8} textTransform="uppercase">
            Navigation
          </Typography>
        </Box>
        <MenuItem
          onClick={() => { setCurrentPageName("Home"); handleClose(); }}
          component={Link}
          to="/"
          sx={{ mt: 0.5 }}
        >
          <ListItemIcon><EmojiEvents fontSize="small" sx={{ color: "#00395A" }} /></ListItemIcon>
          <ListItemText>Home</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => { setCurrentPageName("My Dogs"); handleClose(); }}
          component={Link}
          to="/dogs"
        >
          <ListItemIcon><Pets fontSize="small" sx={{ color: "#00395A" }} /></ListItemIcon>
          <ListItemText>My Dogs</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => { setCurrentPageName("My Handlers"); handleClose(); }}
          component={Link}
          to="/handlers"
        >
          <ListItemIcon><DirectionsRun fontSize="small" sx={{ color: "#00395A" }} /></ListItemIcon>
          <ListItemText>My Handlers</ListItemText>
        </MenuItem>
        {featureFlags.clubMemberships && (
          <MenuItem
            onClick={() => { setCurrentPageName("Club Memberships"); handleClose(); }}
            component={Link}
            to="/club-memberships"
          >
            <ListItemIcon><Groups fontSize="small" sx={{ color: "#00395A" }} /></ListItemIcon>
            <ListItemText>Club Memberships</ListItemText>
          </MenuItem>
        )}
        {showClubAdminNav && (
          <MenuItem
            onClick={() => { setCurrentPageName("Club Administration"); handleClose(); }}
            component={Link}
            to="/agility-clubs"
          >
            <ListItemIcon><ManageAccounts fontSize="small" sx={{ color: "#00395A" }} /></ListItemIcon>
            <ListItemText>Club Administration</ListItemText>
          </MenuItem>
        )}
        {userIsAdmin && (
          <MenuItem
            onClick={() => { setCurrentPageName("Show Management"); handleClose(); }}
            component={Link}
            to="/show-management"
          >
            <ListItemIcon><AdminPanelSettings fontSize="small" sx={{ color: "#00395A" }} /></ListItemIcon>
            <ListItemText>Show Management</ListItemText>
          </MenuItem>
        )}
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={handleClose} component={Link} to="/pricing">
          <ListItemIcon><Sell fontSize="small" sx={{ color: "text.secondary" }} /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ color: "text.secondary" }}>Pricing</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleClose} component={Link} to="/privacy-policy">
          <ListItemIcon><Policy fontSize="small" sx={{ color: "text.secondary" }} /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ color: "text.secondary" }}>Privacy Policy</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => { handleLogout(); setLoggedIn(false); }}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon><LogoutIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default TopNav;
